/**
 * Alat volume serpih — indikatornya, lengkungnya, dan kedua ujungnya.
 *
 * Satu berkas karena dua layar menanyakan hal yang sama. Rel parameter di
 * ruang kerja menanyakannya sambil melihat kurvanya, dan tahap pertama di
 * layar analisis menanyakannya sambil melihat hasil run sebelumnya — tetapi
 * enam belas indikator dan delapan lengkung itu satu daftar, dan daftar yang
 * disalin adalah daftar yang akan berbeda pada nama kesembilan.
 *
 * Pilihannya tetap dua sumbu sebagaimana di Geolog: *apa* yang dibaca sebagai
 * indikator, dan *bagaimana* indeksnya dibengkokkan menjadi volume.
 */
import { useState } from "react";

import type { ParameterValues } from "../types";
import {
  CurveSourceFields,
  channelsRead,
  sameParam,
  type AvailableCurve,
} from "./CurveSource";
import { Button, Dropdown, Modal, NumberField, ParamCaption } from "./ui";


/**
 * Kurva yang dibaca sebagai indikator serpih.
 *
 * Terpisah dari lengkungnya, sebagaimana di Geolog: yang dipilih di sini
 * adalah *apa* yang dibaca, dan yang dipilih di daftar berikutnya adalah
 * *bagaimana* indeksnya dibengkokkan menjadi volume. Satu daftar datar berisi
 * "Linear GR" dan "Larionov" tidak pernah bisa menyatakan Larionov atas
 * indeks SP.
 */
export const VSH_INDICATORS = [
  { value: "gr", label: "Gamma ray", hint: "GR" },
  { value: "cgr", label: "Uranium-free GR", hint: "CGR" },
  { value: "thorium", label: "Thorium", hint: "TH" },
  { value: "potassium", label: "Potassium", hint: "K" },
  { value: "sp", label: "SP", hint: "SP" },
  { value: "resistivity", label: "Resistivity", hint: "RT · log ratio" },
  { value: "neutron", label: "Neutron", hint: "NPHI" },
  { value: "density", label: "Density", hint: "RHOB" },
  { value: "sonic", label: "Sonic", hint: "DTC" },
  { value: "neutron_density", label: "Neutron–density", hint: "NPHI + RHOB" },
  { value: "neutron_sonic", label: "Neutron–sonic", hint: "NPHI + DTC" },
  { value: "density_sonic", label: "Density–sonic", hint: "RHOB + DTC" },
  { value: "minimum", label: "Minimum of indicators", hint: "the smallest of them" },
  { value: "maximum", label: "Maximum of indicators", hint: "upper bound" },
  { value: "average", label: "Mean of indicators", hint: "all of them, averaged" },
  { value: "median", label: "Median of indicators", hint: "the middle value" },
];

/**
 * Lengkung yang membengkokkan indeks serpih menjadi volume.
 *
 * Urutannya bukan abjad melainkan urutan bacaannya, dari yang paling
 * pesimistis ke yang paling optimistis — itu sendiri keterangan, dan ia
 * satu-satunya cara memilih di antara delapan nama yang tak satu pun
 * menjelaskan dirinya.
 */
export const VSH_METHODS = [
  { value: "linear", label: "Linear (index)", hint: "most pessimistic" },
  { value: "larionov_older", label: "Larionov (older)", hint: "pre-Tertiary rocks" },
  { value: "clavier", label: "Clavier" },
  { value: "steiber_2", label: "Steiber 2", hint: "c = 2" },
  { value: "steiber", label: "Steiber", hint: "c = 3" },
  { value: "steiber_3", label: "Steiber 3", hint: "c = 4" },
  { value: "steiber_c", label: "Steiber (custom c)", hint: "free c" },
  { value: "larionov_tertiary", label: "Larionov (Tertiary)", hint: "most optimistic" },
];

/**
 * Kedua ujung yang dibaca tiap indikator linear.
 *
 * Yang tidak ada di sini membaca ujungnya dari tempat lain: neutron–densitas
 * memakai NPHIsh dan RHOsh milik panel porositas, dan "minimum" memakai
 * ujung setiap indikator sekaligus. Menampilkan sepasang kotak GR di sebelah
 * pilihan yang tidak membacanya adalah kotak yang berbohong.
 */
export const VSH_ENDPOINTS: Record<
  string,
  { clean: keyof ParameterValues; shale: keyof ParameterValues;
    cleanLabel: string; shaleLabel: string; unit: string; step: number }
> = {
  gr: { clean: "gr_clean", shale: "gr_shale", cleanLabel: "clean GR",
        shaleLabel: "shale GR", unit: "gAPI", step: 1 },
  cgr: { clean: "cgr_clean", shale: "cgr_shale", cleanLabel: "clean CGR",
         shaleLabel: "shale CGR", unit: "gAPI", step: 1 },
  thorium: { clean: "th_clean", shale: "th_shale", cleanLabel: "clean Th",
             shaleLabel: "shale Th", unit: "ppm", step: 0.5 },
  potassium: { clean: "k_clean", shale: "k_shale", cleanLabel: "clean K",
               shaleLabel: "shale K", unit: "%", step: 0.1 },
  sp: { clean: "sp_clean", shale: "sp_shale", cleanLabel: "sand SP",
        shaleLabel: "shale SP", unit: "mV", step: 1 },
  resistivity: { clean: "rt_clean", shale: "rt_shale", cleanLabel: "clean RT",
                 shaleLabel: "shale RT", unit: "ohm·m", step: 0.5 },
  neutron: { clean: "nphi_clean", shale: "nphi_shale", cleanLabel: "clean NPHI",
             shaleLabel: "shale NPHI", unit: "v/v", step: 0.01 },
  density: { clean: "rhob_clean", shale: "rho_shale", cleanLabel: "clean RHOB",
             shaleLabel: "shale RHOB", unit: "g/cc", step: 0.01 },
  sonic: { clean: "dt_clean", shale: "dt_shale", cleanLabel: "clean DT",
           shaleLabel: "shale DT", unit: "us/ft", step: 1 },
};

/**
 * Keterangan bagi pilihan yang tidak memunculkan sepasang kotak ujung.
 *
 * Sebuah pilihan yang tidak menampilkan apa-apa ketika dipilih membaca
 * seperti pilihan yang tidak bekerja. Yang perlu diketahui justru di mana
 * angkanya berada — pasangan silang membacanya dari panel Porositas — atau
 * bahwa ia memang tidak membaca ujung mana pun.
 */
export const VSH_NOTES: Record<string, string> = {
  neutron_density:
    "The neutron–density separation uses NPHIsh, RHOsh, RHOma and RHOfl from the Porosity panel.",
  neutron_sonic:
    "The neutron–sonic separation uses NPHIsh, DTsh, DTma and DTfl from the Porosity " +
    "panel. The pair to use when it is RHOB that is in doubt: a caved hole ruins the " +
    "density long before it ruins the sonic.",
  density_sonic:
    "The density–sonic separation uses RHOsh, DTsh, and the matrix and fluid values from " +
    "the Porosity panel. The one crossed pair that needs no neutron, so it is open to old " +
    "wells that carry no such curve.",
  minimum:
    "The smallest of every indicator whose curve exists. Each indicator has its own " +
    "reason to read high — uranium in clean sand, an invaded zone, gas crossover — and " +
    "not one of them has a reason to read low without cause.",
  maximum:
    "The largest of every indicator whose curve exists. An upper bound for checking the " +
    "spread between indicators, not for use as a result.",
  average:
    "The mean of every indicator whose curve exists. One indicator that goes astray pulls " +
    "it along by its share.",
  median:
    "The middle value of every indicator whose curve exists. One indicator that goes " +
    "astray does not pull it — that is the difference from the mean.",
};

/**
 * Lengkung tiap metode, ditulis sebagaimana ia dihitung di mesin.
 *
 * Delapan nama yang tak satu pun menjelaskan dirinya; rumusnya yang
 * menjelaskan, dan ia yang membuat "Steiber 2" dan "Steiber 3" bisa
 * dibedakan tanpa membuka buku. `I` adalah indeks serpih, 0..1.
 */
export const VSH_FORMULAS: Record<string, string> = {
  linear: "Vsh = I",
  larionov_older: "Vsh = 0.33 (2^2I − 1)",
  clavier: "Vsh = 1.7 − √(3.38 − (I + 0.7)²)",
  steiber_2: "Vsh = I / (2 − I)",
  steiber: "Vsh = I / (3 − 2I)",
  steiber_3: "Vsh = I / (4 − 3I)",
  steiber_c: "Vsh = I / (c − (c−1) I)",
  larionov_tertiary: "Vsh = 0.083 (2^3.7I − 1)",
};

/** Nama indikator sebagaimana ia disebut di kalimat, bukan di menu. */
export const vshIndicatorName = (indicator: string): string =>
  VSH_INDICATORS.find((option) => option.value === indicator)?.label ?? indicator;

/** Nama lengkungnya, dipakai baik oleh menu maupun oleh pesan hasilnya. */
export const vshMethodName = (method: string): string =>
  VSH_METHODS.find((option) => option.value === method)?.label ?? method;

/**
 * Kedua sumbu pilihan beserta angka yang dibacanya.
 *
 * Dipakai apa adanya di rel ruang kerja, dan di dalam dialog tahap pertama
 * layar analisis. Tidak menyimpan apa pun sendiri: yang memanggil yang
 * memutuskan ke mana sebuah perubahan pergi — ke tumpukan override yang
 * belum dijalankan, atau ke draf sebuah dialog.
 */
export function VshFields({
  params,
  available,
  onChange,
  disabled = false,
}: {
  params: ParameterValues;
  /** Mnemonik yang ada di set kurva aktif sumur ini. Tanpa itu tidak ada
   *  pemilih sumber sama sekali — rel ruang kerja memanggil komponen ini
   *  di tempat yang belum memuat daftar kurvanya. */
  available?: AvailableCurve[];
  onChange: (
    key: keyof ParameterValues,
    value: number | string | boolean | Record<string, string>,
  ) => void;
  disabled?: boolean;
}) {
  const indicator = String(params.vsh_indicator ?? "gr");
  const ends = VSH_ENDPOINTS[indicator];
  return (
    <>
      {/* Indikatornya dulu, lengkungnya kemudian — urutan yang sama dengan
          urutan hitungannya, dan urutan pertanyaan yang ditanyakan orang
          kepada dirinya sendiri: kurva apa yang dipercaya di sumur ini, lalu
          seberapa jauh bacaannya dikoreksi. */}
      {/* Keterangannya di atas kotaknya, bukan di sebelahnya: `label` sebaris
          menaruh dua elemen dalam satu baris lentur, dan sebuah kotak selebar
          penuh di sebelahnya mendorong seluruh panel keluar dari rel selebar
          300 piksel — judul panelnya sendiri ikut terpotong. */}
      <ParamCaption>Indicator</ParamCaption>
      <Dropdown
        value={indicator}
        options={VSH_INDICATORS}
        onChange={(value) => onChange("vsh_indicator", value)}
        width="100%"
        disabled={disabled}
      />
      <ParamCaption>Transform</ParamCaption>
      <Dropdown
        value={String(params.vsh_method)}
        options={VSH_METHODS}
        onChange={(value) => onChange("vsh_method", value)}
        width="100%"
        disabled={disabled}
      />
      {VSH_FORMULAS[String(params.vsh_method)] && (
        <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-4)" }}>
          {VSH_FORMULAS[String(params.vsh_method)]}
        </span>
      )}
      {/* Kotaknya mengikuti indikator yang sedang dipilih. */}
      {ends && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <NumberField
            label={ends.cleanLabel}
            unit={ends.unit}
            value={Number(params[ends.clean])}
            step={ends.step}
            onChange={(v) => onChange(ends.clean, v)}
            disabled={disabled}
          />
          <NumberField
            label={ends.shaleLabel}
            unit={ends.unit}
            value={Number(params[ends.shale])}
            step={ends.step}
            onChange={(v) => onChange(ends.shale, v)}
            disabled={disabled}
          />
        </div>
      )}
      {VSH_NOTES[indicator] && (
        <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.5 }}>
          {VSH_NOTES[indicator]}
        </span>
      )}
      {params.vsh_method === "steiber_c" && (
        <NumberField
          label="Coefficient c"
          value={Number(params.vsh_steiber_c ?? 3)}
          step={0.1}
          min={1}
          onChange={(v) => onChange("vsh_steiber_c", v)}
          disabled={disabled}
        />
      )}
      {available && (
        <CurveSourceFields
          params={params}
          available={available}
          channels={channelsRead(1, params)}
          onChange={(sources) => onChange("curve_sources", sources)}
          disabled={disabled}
        />
      )}

    </>
  );
}

/** Kunci yang boleh berangkat dari dialog ini; sisanya bukan urusan Vsh. */
const VSH_KEYS: (keyof ParameterValues)[] = [
  "vsh_indicator", "vsh_method", "vsh_steiber_c",
  ...Object.values(VSH_ENDPOINTS).flatMap((e) => [e.clean, e.shale]),
  "curve_sources",
];

/**
 * Tahap Vsh sebagai jendelanya sendiri.
 *
 * Yang dikirim pulang hanyalah yang benar-benar diubah, dan hanya di antara
 * kunci Vsh: sebuah dialog yang mengirim seluruh set parameter akan menerbitkan
 * versi baru yang menyebut lima belas angka berubah padahal seorang pembaca
 * hanya menukar satu lengkung.
 */
export function VshDialog({
  params,
  available,
  busy = false,
  disabled = false,
  onClose,
  onRun,
}: {
  params: ParameterValues;
  available?: AvailableCurve[];
  busy?: boolean;
  disabled?: boolean;
  onClose: () => void;
  onRun: (overrides: Partial<ParameterValues>) => void;
}) {
  const [draft, setDraft] = useState<Partial<ParameterValues>>({});
  const merged = { ...params, ...draft } as ParameterValues;
  // Ujung milik indikator yang tidak jadi dipilih tidak ikut berangkat: yang
  // tersisa dari percobaan sebelumnya adalah angka yang tidak menentukan
  // apa pun pada hasil ini.
  const changed = Object.fromEntries(
    VSH_KEYS.filter((key) => key in draft && !sameParam(draft[key], params[key])).map(
      (key) => [key, draft[key]],
    ),
  ) as Partial<ParameterValues>;

  return (
    <Modal
      title="1 · Shale volume"
      width={420}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={busy}
            disabled={disabled}
            onClick={() => onRun(changed)}
          >
            Compute Vsh
          </Button>
        </>
      }
    >
      <VshFields
        params={merged}
        available={available}
        onChange={(key, value) => setDraft((current) => ({ ...current, [key]: value }))}
        disabled={disabled || busy}
      />
      <span style={{ font: "400 9.5px/1.5 var(--font-sans)", color: "var(--ink-6)" }}>
        {`Vsh is computed as ${vshMethodName(String(merged.vsh_method))} over the ` +
          `${vshIndicatorName(String(merged.vsh_indicator ?? "gr"))} index. ` +
          "The whole deterministic workflow is run again, because effective porosity, " +
          "saturation and the cutoffs all read Vsh."}
      </span>
    </Modal>
  );
}
